import { ExclamationCircleOutlined } from "@ant-design/icons"
import {
    ApprovalProcess,
    ApprovalScopeEnum,
    ApprovalStatusEnum,
    ApproverTypeEnum,
    CostCenter2,
    Employee,
    ExpenseKindEnum,
    ExpensePaymentFlowInformationEnum,
    ExpenseStatusEnum,
    RightEnum,
    SubscriptionTypeEnum,
    UserRule,
} from "@finway-group/shared/lib/models"
import { ExpensePaymentOptionEnum } from "@finway-group/shared/lib/models/expense/expensePaymentOption.enum"
import { GetEmployeeById } from "@finway-group/shared/lib/models/user/employee.model"
import { getApprovalProcess, getApprovalProcessScopeFromExpenseStatus } from "@finway-group/shared/lib/utils/approvalProcess.utils"
import { Alert, Button, Col, DatePicker, Form, Row, Select, Tooltip } from "antd"
import moment from "moment"
import React, { useEffect, useMemo, useRef } from "react"
import { Plus as PlusIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { ApprovalProcessIndicatorTag } from "~Components/approvalProcessesIndicator"
import { ExpenseTagSection } from "~Components/ExpenseTagSection"
import { CostCenter2Select } from "~Components/form/CostCenter2Select"
import { CostCenterSelect } from "~Components/form/CostCenterSelect"
import { ExpenseAccountSelect } from "~Components/form/ExpenseAccountSelect"
import { FormSelect } from "~Components/form/formSelect"
import { ExpenseRules } from "~Components/forms/rules"
import { CostCenter2Modal } from "~Components/modals/costCenter2.modal"
import { EmployeeRuleModal } from "~Components/modals/employeeRule.modal"
import { useExpenseFormContext } from "~Components/modals/expenseCreateForm.context"
import { UserImage } from "~Components/UserImage"
import { NOT_SET_VALUE } from "~Shared/config/consts"
import { PROCESS_ENV } from "~Shared/config/env.utils"
import { useModal } from "~Shared/context/modal.context"
import { useCompany } from "~Shared/hooks/company.hooks"
import { useSubCostCenters } from "~Shared/hooks/costCenter.hooks"
import { useCostCenters2 } from "~Shared/hooks/costCenter2.hooks"
import { useLoggedInEmployee } from "~Shared/hooks/employee.hooks"
import { useExpense, useIsExpenseAccountMandatory } from "~Shared/hooks/expense.hooks"
import { useExpenseAccounts } from "~Shared/hooks/expenseAccount.hooks"
import { useWorkflowById } from "~Shared/hooks/workflow.hooks"
import { useEmployees } from "~Shared/queries/employee.queries"
import { ApprovalProcessService, AuthzService, ExpenseService } from "~Shared/services"
import { gobdNonCompliantDates } from "~Shared/utils/date.utils"
import { getSubstitutionTooltipMessage } from "~Shared/utils/employee.utils"
import { changeAllSplitPropertyValues, checkIsExpenseAccountMandatoryForExpenseStatus, isFolderExpense } from "~Shared/utils/expense.utils"
import { doAllElementsHaveTheSameFieldValue, doFilterSelect, insertIf, isExpensePaymentFlowInfoEnabled } from "~Shared/utils/helper.utils"
import { getPopupAnchor } from "~Shared/utils/popup.utils"
import { CurrencySelect } from "./CurrencySelect"

interface ExpenseCommonInputFieldsFormProps {
    allowPaymentFlow?: boolean
    showCurrency?: boolean
    showPerformancePeriod?: boolean
}
const MINIMUM_EMPLOYEE_RULES = 1

export const ExpenseCommonInputFieldsForm = ({ allowPaymentFlow = true, showCurrency = true, showPerformancePeriod = true }: ExpenseCommonInputFieldsFormProps) => {
    const [{ expenseForm, expense, isPreApprovedSubscription, isNew, updateExpense, folder, isBuildingApprovalProcesses }] = useExpenseFormContext()

    const isExpenseAccountMandatory = useIsExpenseAccountMandatory()

    const loggedInUser = useLoggedInEmployee()
    const { data: employees = [] } = useEmployees({ includeAutoApprover: true })
    const costCenters = useSubCostCenters(true)
    const { t } = useTranslation()
    const { showModal } = useModal()
    const { costCenter, requestedBy, approvalNeededBy, expenseAccount } = ExpenseRules({ expense, employees })
    const company = useCompany()
    const scope =
        getApprovalProcessScopeFromExpenseStatus(expense.status, true) ??
        (isPreApprovedSubscription && expense.status === ExpenseStatusEnum.DONE ? ApprovalScopeEnum.EXPENSE_INVOICE : undefined)

    const approvalProcess = scope ? getApprovalProcess(scope, expense.approvalProcesses) : undefined
    const workflow = useWorkflowById(approvalProcess?.workflow)

    const isCardExpense = expense.paymentOption === ExpensePaymentOptionEnum.SMART_CARD
    const isLoggedInUserPotentialApprover = AuthzService.isLoggedInUserPotentialApprover()
    const showPaymentFlowInformation = allowPaymentFlow
        ? !isCardExpense && !expense.isReimbursement && isExpensePaymentFlowInfoEnabled(company, isLoggedInUserPotentialApprover)
        : false
    const isApproverChangeDisabled =
        !!folder ||
        !!expense.folderId ||
        (expense.paymentFlowInformation &&
            [ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID, ExpensePaymentFlowInformationEnum.REPORTING_ONLY].includes(expense.paymentFlowInformation)) ||
        (!!approvalProcess?.workflow && !company?.allowWorkflowAdjustments && !AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__ADJUST_WORKFLOW))

    const expenseAccounts = useExpenseAccounts()
    const costCenters2 = useCostCenters2()
    const { splits } = expense
    const isCostCenterChangeable = doAllElementsHaveTheSameFieldValue(splits, "costCenter")
    const isCostCenter2Changeable = doAllElementsHaveTheSameFieldValue(splits, "costCenter2")
    const isExpenseAccountChangeable = doAllElementsHaveTheSameFieldValue(
        splits.map((s) => s.expenseAccount),
        "_id",
    )
    const { showCostCentersIds, taggingEnabled } = useCompany()
    const expenseDetails = useExpense()
    const allowCostCenter2Creation = useCompany()?.allowCostCenter2Creation

    const requester = useMemo(() => GetEmployeeById(employees, expense.requestedBy._id), [employees, expense.requestedBy._id])

    const isPaymentFlowInfoEditDisabled =
        !isNew &&
        !expenseDetails.isInitialSubscription() &&
        (expenseDetails.paymentFlowInformation === ExpensePaymentFlowInformationEnum.REPORTING_ONLY || expenseDetails.isReviewed() || expenseDetails.isProcessed())

    const purchaseApprovalProcess = getApprovalProcess(ApprovalScopeEnum.EXPENSE_PURCHASE, expense.approvalProcesses)
    const isDonePurchaseParentSub = expense.subscriptionType === SubscriptionTypeEnum.INITIAL_REQUEST && expense.status === ExpenseStatusEnum.DONE && !!purchaseApprovalProcess

    const proposedApprovers = useMemo(
        () => ExpenseService.determinePossibleApproversForExpense(expense, employees, costCenters, loggedInUser, isPreApprovedSubscription, true),
        [expense, employees, costCenters, loggedInUser, isPreApprovedSubscription],
    )

    const onCostCenterChange = (value: any) => {
        const splitsUpdate = [...splits]
        changeAllSplitPropertyValues({ splits: splitsUpdate, propertyToChange: "costCenter", newValue: value, kind: expense.kind as ExpenseKindEnum })
        updateExpense({ costCenter: value, splits: splitsUpdate })
    }

    const onPaymentFlowInfoChanged = (value: any) => {
        updateExpense({ paymentFlowInformation: value })
    }

    const onCostCenter2Change = (value: any) => {
        const splitsUpdate = [...splits]
        changeAllSplitPropertyValues({ splits: splitsUpdate, propertyToChange: "costCenter2", newValue: value, kind: expense.kind as ExpenseKindEnum })
        updateExpense({ costCenter2: value, splits: splitsUpdate })
    }

    const onExpenseAccountChange = (value: any) => {
        const splitsUpdate = [...splits]
        changeAllSplitPropertyValues({ splits: splitsUpdate, propertyToChange: "expenseAccount", newValue: { _id: value }, kind: expense.kind as ExpenseKindEnum })
        updateExpense({ expenseAccount: { _id: value }, splits: splitsUpdate })
    }

    const onRequestedByChange = (value: any) => {
        updateExpense({ requestedBy: { _id: value } })
    }

    const onApproverChange = async (value: any) => {
        /**
          isDoneParentSub is added to give the possibility to the user to change the approver for the subscription and it's recurring expenses
          (check https://levaroio.atlassian.net/browse/CS-2057 for context) 
        */
        const scopes = [
            ...insertIf(expense.status === ExpenseStatusEnum.PURCHASE_PENDING || isDonePurchaseParentSub, ApprovalScopeEnum.EXPENSE_PURCHASE),
            ApprovalScopeEnum.EXPENSE_INVOICE,
        ]
        const newApprovalProcesses = []
        for (const scope of scopes) {
            const approvalProcess = getApprovalProcess(scope, expense.approvalProcesses)
            const newApprovalProcess: ApprovalProcess = {
                scope,
                status: isPreApprovedSubscription ? ApprovalStatusEnum.APPROVED : ApprovalStatusEnum.NOT_STARTED,
                steps: [
                    {
                        status: isPreApprovedSubscription ? ApprovalStatusEnum.APPROVED : ApprovalStatusEnum.NOT_STARTED,
                        approvalExpression: { approverType: ApproverTypeEnum.USER, approver: { _id: value } },
                    },
                ],
                workflow: null,
                buildIssues: approvalProcess?.buildIssues,
            }

            if (!approvalProcess) {
                newApprovalProcesses.push(newApprovalProcess)
                continue
            }

            // If expense is a pre-approved condition, replace the approval process.
            if (isPreApprovedSubscription) {
                newApprovalProcesses.push(newApprovalProcess)
                continue
            }

            // If approval process is rejected do not replace the approval process.
            if (approvalProcess.status === ApprovalStatusEnum.REJECTED) {
                continue
            }

            // If approval process is approved do not replace the approval process.
            if (approvalProcess.status === ApprovalStatusEnum.APPROVED) {
                continue
            }

            // If we are modifying the approver while in purchase pending, only modify the invoice approval process if it has no workflow.
            if (approvalProcess.scope === ApprovalScopeEnum.EXPENSE_INVOICE && expense.status === ExpenseStatusEnum.PURCHASE_PENDING) {
                if (approvalProcess.workflow) {
                    continue
                }
            }

            newApprovalProcesses.push(newApprovalProcess)
        }

        const approvalProcesses = await ApprovalProcessService.getMergedApprovalProcesses(expense.approvalProcesses, newApprovalProcesses, {
            prioritizeApproved: isPreApprovedSubscription,
        })

        updateExpense({ approvalProcesses })
    }

    const onCostCenter2Creation = (_flag: boolean, costCenter2: CostCenter2) => {
        if (costCenter2) {
            // that means we created a new one
            expenseForm.setFieldsValue({ costCenter2: costCenter2._id })
            updateExpense({ costCenter2: costCenter2._id })
        }
    }

    const showEmployeeRuleModal = () => {
        showModal(EmployeeRuleModal, true, {
            isShowing: true,
            employeeIDToUpdate: expense.requestedBy._id,
            onSubmit: (rules: Pick<UserRule, "costCenter" | "costCenter2" | "expenseAccount">) => {
                expense.kind !== ExpenseKindEnum.HOSPITALITY &&
                    updateExpense({
                        costCenter: rules.costCenter,
                        costCenter2: rules.costCenter2,
                        expenseAccount: rules.expenseAccount ? { _id: rules.expenseAccount } : undefined,
                    })
            },
        })
    }

    const showCostCenter2Modal = () => {
        showModal(CostCenter2Modal, true, { isShowing: true, isNew: true, shouldRedirectOnRestoration: false, onCancel: onCostCenter2Creation })
    }
    const renderPaymentFlowSelect = (width: number) => (
        <Col span={width} sm="24">
            <Form.Item label={t("input:request.payment_flow_information.title")} key="paymentFlowInformation" name="paymentFlowInformation">
                <Select
                    getPopupContainer={getPopupAnchor()}
                    defaultValue={NOT_SET_VALUE}
                    value={expense.paymentFlowInformation ?? NOT_SET_VALUE}
                    onSelect={onPaymentFlowInfoChanged}
                    disabled={isPaymentFlowInfoEditDisabled}
                >
                    {(company?.expensePaymentFlowInformation?.isAlreadyPaidEnabled || expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.ALREADY_PAID) && (
                        <Select.Option value={ExpensePaymentFlowInformationEnum.ALREADY_PAID}>
                            {t(`input:request.payment_flow_information.${ExpensePaymentFlowInformationEnum.ALREADY_PAID}`)}
                        </Select.Option>
                    )}
                    {(company?.expensePaymentFlowInformation?.isAlreadyApprovedAndPaidEnabled ||
                        expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID) && (
                        <Select.Option value={ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID}>
                            {t(`input:request.payment_flow_information.${ExpensePaymentFlowInformationEnum.ALREADY_APPROVED_AND_PAID}`)}
                        </Select.Option>
                    )}
                    {((AuthzService.isLoggedInUserAllowedToApproveExpense(expense) && company?.expensePaymentFlowInformation?.isReportingOnlyEnabled) ||
                        expense.paymentFlowInformation === ExpensePaymentFlowInformationEnum.REPORTING_ONLY) && (
                        <Select.Option value={ExpensePaymentFlowInformationEnum.REPORTING_ONLY}>
                            {t(`input:request.payment_flow_information.${ExpensePaymentFlowInformationEnum.REPORTING_ONLY}`)}
                        </Select.Option>
                    )}
                    <Select.Option value={NOT_SET_VALUE}>{t("input:request.payment_flow_information.not_set")}</Select.Option>
                </Select>
            </Form.Item>
        </Col>
    )

    const getUserRuleCount = () => {
        const rules = employees.find((employee) => employee.id === expense.requestedBy._id)?.rule?.find(({ companyId }) => companyId === company._id) ?? {}
        const numberOfRules = Object.values(rules).filter((rule) => rule !== null).length
        // if rules is not the empty object, it includes the id field and the companyId which are not rules and are therefore not included in the rule count
        return numberOfRules === 0 ? numberOfRules : numberOfRules - 2
    }

    const userLabel = () => (
        <>
            {expense.requestedBy ? (
                <span className="cursor-pointer" onClick={showEmployeeRuleModal}>
                    {`${t("input:request.requested_by")} `}
                    <u className="rule-link">{`(${t("label:rules")} ${getUserRuleCount()}/${
                        MINIMUM_EMPLOYEE_RULES + Number(costCenters2.length > 0) + Number(expenseAccounts.length > 0)
                    })`}</u>
                </span>
            ) : (
                <span>{t("input:request.requested_by")}</span>
            )}
        </>
    )

    const anchorRef = useRef(null)

    useEffect(() => {
        if (!isCostCenterChangeable && expense.costCenter) {
            updateExpense({ costCenter: undefined }) // set to splitted
        }
        if (!isCostCenter2Changeable && expense.costCenter2) {
            updateExpense({ costCenter2: undefined }) // set to splitted
        }
        if (!isExpenseAccountChangeable && expense.expenseAccount?._id !== undefined) {
            updateExpense({ expenseAccount: undefined }) // set to splitted
        }
    }, [isCostCenterChangeable, isCostCenter2Changeable, isExpenseAccountChangeable, updateExpense])

    return (
        <>
            <Row gutter={[16, 16]} align="top">
                {showPerformancePeriod && (
                    <Col span={showCurrency ? 12 : 24}>
                        <Form.Item data-testid="performancePeriod" label={t("label:performance_period")} name="performancePeriod" className="text-opacity-50">
                            <DatePicker.RangePicker
                                name="performancePeriod"
                                disabledDate={gobdNonCompliantDates}
                                style={{ width: "100%" }}
                                format={moment.localeData().longDateFormat("L")}
                                placeholder={[t("placeholder:start_date"), t("placeholder:end_date")]}
                                getPopupContainer={getPopupAnchor()}
                                onChange={(values) => updateExpense({ performancePeriod: values })}
                            />
                        </Form.Item>
                    </Col>
                )}
                {showCurrency && (
                    <Col span={showPerformancePeriod ? 12 : 24} sm="24" md="24">
                        <CurrencySelect onCurrencyChange={updateExpense} />
                    </Col>
                )}
            </Row>
            <Row gutter={[16, 16]} align="top" className="items-center">
                <Col data-testid="expenseCostCenter" span={12} sm="24">
                    <Form.Item label={t("input:request.cost_center")} key="costCenter" name="costCenter" required rules={isCostCenterChangeable ? costCenter : undefined}>
                        <CostCenterSelect
                            costCenters={costCenters}
                            showCostCentersIds={showCostCentersIds}
                            value={expense.costCenter}
                            onSelect={onCostCenterChange}
                            getPopupAnchor={getPopupAnchor}
                            disabled={!isCostCenterChangeable}
                            placeholder={!isCostCenterChangeable ? t("label:splitted") : t("placeholder:cost_center.select_a_cost_center")}
                        />
                    </Form.Item>
                </Col>
                {(allowCostCenter2Creation || costCenters2.length > 0) && (
                    <>
                        <Col span={allowCostCenter2Creation ? 10 : 12}>
                            <Form.Item label={t("label:cost_center_2")} key="costCenter2" name="costCenter2">
                                <CostCenter2Select
                                    costCenters2={costCenters2}
                                    getPopupAnchor={getPopupAnchor}
                                    onSelect={onCostCenter2Change}
                                    value={expense.costCenter2 || NOT_SET_VALUE}
                                    disabled={!isCostCenter2Changeable}
                                    placeholder={!isCostCenter2Changeable ? t("label:splitted") : t("placeholder:cost_center_2.select_a_cost_center_2")}
                                />
                            </Form.Item>
                        </Col>
                        {allowCostCenter2Creation && (
                            <Col span={2}>
                                <Form.Item className="mt-10">
                                    <Button className="btn-default mt-22" onClick={() => showCostCenter2Modal()}>
                                        <PlusIcon />
                                    </Button>
                                </Form.Item>
                            </Col>
                        )}
                    </>
                )}
            </Row>
            {(allowCostCenter2Creation || costCenters2.length > 0) && <Row gutter={[16, 16]} align="top"></Row>}
            {expenseAccounts.length > 0 ? (
                <Row gutter={[16, 16]} align="top">
                    <Col span={!showPaymentFlowInformation ? 24 : 12} sm="24">
                        <Form.Item name={["expenseAccount", "_id"]} label={t("input:request.expense_account")} rules={expenseAccount ?? []} preserve required={false}>
                            <ExpenseAccountSelect
                                value={expense.expenseAccount?._id || NOT_SET_VALUE}
                                expenseAccounts={expenseAccounts}
                                onSelect={onExpenseAccountChange}
                                disabled={!isExpenseAccountChangeable}
                                placeholder={!isExpenseAccountChangeable ? t("label:splitted") : undefined}
                                showNotSetOption={
                                    !checkIsExpenseAccountMandatoryForExpenseStatus({
                                        expenseStatus: expense.status,
                                        isExpenseAccountMandatory,
                                        isFolder: isFolderExpense(expense),
                                    })
                                }
                            />
                        </Form.Item>
                    </Col>
                    {showPaymentFlowInformation && renderPaymentFlowSelect(12)}
                </Row>
            ) : (
                showPaymentFlowInformation && (
                    <Row gutter={[16, 16]} align="top">
                        {renderPaymentFlowSelect(24)}
                    </Row>
                )
            )}

            {taggingEnabled && (
                <Row gutter={[16, 16]} align="top">
                    <div className="flex items-center py-10 flex-wrap">
                        <ExpenseTagSection
                            expenseId={expense.id}
                            inCreation={true}
                            expenseTags={expense.expenseTags}
                            onTagChange={(id: string, detach: boolean) => {
                                const expenseTags = detach ? expense.expenseTags.filter((tag) => tag !== id) : [...expense.expenseTags, id]
                                updateExpense({ expenseTags })
                            }}
                        />
                    </div>
                </Row>
            )}
            {(scope || isPreApprovedSubscription) && (
                <Row gutter={[16, 16]} className="flex-wrap">
                    {workflow && !isBuildingApprovalProcesses && (
                        <Col flex={1} className="flex-grow">
                            <Form.Item label={<p>{t("input:request.workflow")}</p>} key="workflow" name="workflow">
                                <Select className="mb-5" defaultValue={workflow.name} disabled data-testid="finway:expenseCommonInput:workflowNameSelect" />
                            </Form.Item>
                        </Col>
                    )}
                    <Col flex={1} className="flex-grow" ref={anchorRef}>
                        <Form.Item
                            labelCol={{ className: "h-30" }}
                            label={
                                <div className="flex gap-10">
                                    <p className="inline-block">
                                        {isPreApprovedSubscription ? (
                                            <>
                                                {t("input:request.recurring_expense_approval_process")}
                                                <Tooltip
                                                    getPopupContainer={getPopupAnchor(anchorRef.current)}
                                                    title={t("tooltips:approval_process_will_apply_to_recurring_expenses")}
                                                    placement="right"
                                                    className="align-middle"
                                                >
                                                    <ExclamationCircleOutlined className="ml-6" />
                                                </Tooltip>
                                            </>
                                        ) : (
                                            t("input:request.next_approver")
                                        )}
                                    </p>
                                    <ApprovalProcessIndicatorTag scope={scope} approvalProcesses={expense.approvalProcesses} showLoadingSpinner={isBuildingApprovalProcesses} />
                                </div>
                            }
                            key="nextApprover"
                            name="nextApprover"
                            rules={approvalNeededBy}
                        >
                            <Select showSearch filterOption={doFilterSelect} onChange={onApproverChange} getPopupContainer={getPopupAnchor()} disabled={isApproverChangeDisabled}>
                                {proposedApprovers.map((employee) => {
                                    const substitutionTooltip = getSubstitutionTooltipMessage(employee, loggedInUser.id, t)
                                    return (
                                        <Select.Option key={employee.id} label={`${employee.firstName} ${employee.lastName}`} value={employee.id}>
                                            <div className="flex items-center ml-3" id={`approver-option-${employee.id}`}>
                                                <UserImage user={employee} showReplacementIcon={!!substitutionTooltip} tooltipTitle={substitutionTooltip} />
                                                <Tooltip title={substitutionTooltip}>
                                                    <span>{`${employee.firstName} ${employee.lastName}`}</span>
                                                </Tooltip>
                                            </div>
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
            )}

            {AuthzService.isRightGrantedForLoggedInUser(RightEnum.EXPENSE__ALL__CREATE_ON_BEHALF) && (
                <div className="flex flex-col gap-6" data-testid="expenseFormRequestedBy">
                    <Form.Item label={userLabel()}>
                        <FormSelect
                            name={["requestedBy", "_id"]}
                            header=""
                            initialValue={loggedInUser.id}
                            isDisabled={!!expense.card}
                            rules={requestedBy}
                            options={employees.filter((employee: Employee) => {
                                if (
                                    employee.email !== PROCESS_ENV.FINWAY_ADMIN_EMAIL &&
                                    !employee.activeCompanyProfile.deleted &&
                                    AuthzService.isAllowedToCreateExpenses(employee.activeCompanyProfile.roleId)
                                ) {
                                    return true
                                }

                                // Fallback if someone does not have right to create expense, but already assigned as the requestedBy, so it doesnt show the ID string
                                if (employee.id === expense.requestedBy._id) {
                                    return true
                                }

                                return false
                            })}
                            extra={expense.card && <p className="input-help">{t("info:cards.cannot_change_requester_with_attached_card")}</p>}
                            onChange={onRequestedByChange}
                            displayImages
                            showSearch
                            keyCode="0"
                        />
                    </Form.Item>

                    {expense.isReimbursement && requester && (
                        <Alert
                            message={
                                <span>
                                    <strong>{requester.name}</strong> {t("info:will_receive_reimbursement_payment")}
                                </span>
                            }
                            type="info"
                            showIcon
                        />
                    )}
                </div>
            )}
        </>
    )
}
